import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonReorder,
  IonReorderGroup,
  IonThumbnail,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '../hooks/authContext';
import useUpdateCtPhotosPositions from '../hooks/updateCtPhotosPositions';
import { ClaimPhotosType } from '../graphql/GetClaimPhotosList';
import useClaimPhotosPagedSorted from '../hooks/claimPhotosPagedSorted';

export interface Props {
  claimIndx: number
  onDidDismiss: () => any
}

export const ReorderSiteInspectionImagesModal: React.FC<Props> = ({ claimIndx, onDidDismiss }) => {
  const { t } = useTranslation();
  const { userState } = useAuth()!;

  const [claimPhotosList, setClaimPhotosList] = useState<ClaimPhotosType[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const pictureHeight = 60;
  const rows = Math.floor((window.innerHeight - 300) / pictureHeight) || 5;
  const pageSize = window.innerWidth > 800 ? rows + 5 : 15;
  const region = userState.userAuthPayload?.regionId!;
  const { isFetching, isLoading, refetch } = useClaimPhotosPagedSorted(
    region,
    claimIndx,
    currentPage,
    pageSize,
    userState,
    claimPhotosList,
    setClaimPhotosList,
    setTotalItems,
  );
  const { mutateAsync } = useUpdateCtPhotosPositions(userState);
  const [isiPhotos, setIsiPhotos] = useState<ClaimPhotosType[]>([]);
  const [showIsLoading, setShowIsLoading] = useState<boolean>(false);

  const loadMore = (event: CustomEvent<void>) => {
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      const total = totalItems;
      if (total > 0 && total <= claimPhotosList.length) {
        setDisableInfiniteScroll(true);
      }

      (event.target as HTMLIonInfiniteScrollElement).complete();
    }, 500);
  };

  useEffect(() => {
    if ((isLoading || isFetching) !== showIsLoading) {
      setShowIsLoading(isLoading || isFetching);
    }
  }, [isFetching, isLoading, showIsLoading]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (claimPhotosList.length > 0) {
      setShowIsLoading(true);
      setIsiPhotos(claimPhotosList.filter(photo => photo.includedInIsi));
      setShowIsLoading(false);
    }
  }, [claimPhotosList]);

  const handleReorder = async (event: any) => {
    const { from, to } = event.detail;
    const [obj] = isiPhotos.splice(from, 1);
    isiPhotos.splice(to, 0, obj);
    event.detail.complete();
    await mutateAsync({
      claimIndx,
      ctPhotoPositions: isiPhotos.map((photo, position) => ({ ctfileId: photo.ctfileId, position })),
    });
  };

  return (
    <IonModal isOpen={claimIndx > 0} onDidDismiss={onDidDismiss} >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('reorderImages').toString()}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDidDismiss}>{t('ok').toString()}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonReorderGroup disabled={false} onIonItemReorder={handleReorder}>
          {!!isiPhotos?.length && isiPhotos?.map((photo) => (<IonItem key={photo.ctfileId}>
            <IonThumbnail slot="start">
              <img alt={photo.description} src={photo.filePath} />
            </IonThumbnail>
            <IonLabel>
              <h3>{`${photo.fileName}.${photo.fileExtension}`}</h3>
              <p>{format(new Date(photo.uploadedDate), "yyyy-MM-dd hh:mm aaaaa'm'")}</p>
            </IonLabel>
            <IonReorder slot="end" />
          </IonItem>))}
          <IonInfiniteScroll
            threshold="100px"
            disabled={disableInfiniteScroll}
            onIonInfinite={loadMore}
          >
            <IonInfiniteScrollContent
              loadingText={t('loadingMore').toString()}
            />
          </IonInfiniteScroll>
          {!isiPhotos?.length && !showIsLoading && <p className="ion-text-center">{t('noPhotos').toString()}</p>}
        </IonReorderGroup>
      </IonContent>
      <IonLoading isOpen={showIsLoading} message={t('loading')} duration={2500} />
    </IonModal>
  );
};

export default ReorderSiteInspectionImagesModal;
